import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { MdStar, MdStarBorder, MdStarHalf } from 'react-icons/md';
import {
  AppCard,
  OutlineButton,
  SortableTh,
  TableLoader,
} from '../../../../components/ui';
import { getVillageSummary } from '../../../../repositories/dashboard.repository';
import { useTableSort } from '../../../../hooks/useTableSort';

function AvgStarRating({ value }) {
  if (value == null || Number.isNaN(Number(value))) {
    return (
      <Text as="span" color="text.secondary">
        —
      </Text>
    );
  }
  const rating = Math.max(0, Math.min(5, Number(value)));
  const full = Math.floor(rating);
  const hasHalf = rating - full >= 0.3 && rating - full < 0.8;
  const roundedHalf = rating - full >= 0.8 ? 1 : 0;
  const filled = full + roundedHalf;
  return (
    <HStack spacing={0.5} title={`${rating.toFixed(1)} / 5`}>
      {[1, 2, 3, 4, 5].map((n) => {
        let icon = MdStarBorder;
        if (n <= filled) icon = MdStar;
        else if (n === filled + 1 && hasHalf && !roundedHalf) icon = MdStarHalf;
        return (
          <Icon
            key={n}
            as={icon}
            boxSize="16px"
            color={icon === MdStarBorder ? 'gray.300' : 'brand.500'}
          />
        );
      })}
      <Text fontSize="12px" color="text.secondary" ml={1}>
        {rating.toFixed(1)}
      </Text>
    </HStack>
  );
}

export default function VillageSummaryPage() {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getVillageSummary();
      setRows(data);
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load village summary',
        status: 'error',
        position: 'top-right',
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.villageName.toLowerCase().includes(q) ||
        r.villageCode.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const { sorted, sort, toggleSort } = useTableSort(
    filtered,
    {
      villageName: (r) => r.villageName,
      villageCode: (r) => r.villageCode,
      equipmentCount: (r) => r.equipmentCount,
      recommendedCount: (r) => r.recommendedCount,
      deployedCount: (r) => r.deployedCount,
      releasedCount: (r) => r.releasedCount,
      avgRating: (r) => (r.avgRating == null ? -1 : r.avgRating),
    },
    { key: 'villageName', dir: 'asc' },
  );

  return (
    <>
      <Flex justify="space-between" align={{ base: 'stretch', md: 'center' }} gap={3} mb={2} wrap="wrap">
        <Box>
          <Heading size="lg">Village summary</Heading>
          <Text color="text.secondary" mt={1}>
            Equipment and recommend / deploy activity by village. Empty villages are hidden.
          </Text>
        </Box>
        <OutlineButton onClick={load} isDisabled={loading}>
          Refresh
        </OutlineButton>
      </Flex>

      <AppCard mb={4}>
        <FormLabel fontSize="13px" mb={1}>
          Filter villages
        </FormLabel>
        <Input
          placeholder="Search by village name or code…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          maxW="360px"
        />
      </AppCard>

      <AppCard p={0} overflow="hidden">
        <Flex px={5} py={3} borderBottomWidth="1px" borderColor="border.subtle" align="center">
          <Text fontSize="13px" color="text.secondary">
            {loading
              ? 'Loading…'
              : `${sorted.length} village${sorted.length === 1 ? '' : 's'} with data`}
          </Text>
        </Flex>
        {loading ? (
          <TableLoader rows={6} cols={6} />
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" size="md">
              <Thead bg="bg.input">
                <Tr>
                  <SortableTh columnKey="villageName" sort={sort} onSort={toggleSort}>
                    Village
                  </SortableTh>
                  <SortableTh columnKey="equipmentCount" sort={sort} onSort={toggleSort}>
                    Equipments
                  </SortableTh>
                  <SortableTh columnKey="recommendedCount" sort={sort} onSort={toggleSort}>
                    Recommended
                  </SortableTh>
                  <SortableTh columnKey="deployedCount" sort={sort} onSort={toggleSort}>
                    Deployed
                  </SortableTh>
                  <SortableTh columnKey="releasedCount" sort={sort} onSort={toggleSort}>
                    Released
                  </SortableTh>
                  <SortableTh columnKey="avgRating" sort={sort} onSort={toggleSort}>
                    Avg rating
                  </SortableTh>
                </Tr>
              </Thead>
              <Tbody>
                {sorted.map((r) => (
                  <Tr key={r.villageId || r.villageCode}>
                    <Td>
                      <Text fontWeight="600">{r.villageName || '—'}</Text>
                      {r.villageCode ? (
                        <Text fontSize="12px" color="text.secondary" fontFamily="mono">
                          {r.villageCode}
                        </Text>
                      ) : null}
                    </Td>
                    <Td fontWeight="600">{r.equipmentCount}</Td>
                    <Td>{r.recommendedCount}</Td>
                    <Td>{r.deployedCount}</Td>
                    <Td>{r.releasedCount}</Td>
                    <Td>
                      <AvgStarRating value={r.avgRating} />
                    </Td>
                  </Tr>
                ))}
                {sorted.length === 0 ? (
                  <Tr>
                    <Td colSpan={6}>
                      <Text color="text.secondary" py={8} textAlign="center">
                        No villages with equipment or activity yet.
                      </Text>
                    </Td>
                  </Tr>
                ) : null}
              </Tbody>
            </Table>
          </Box>
        )}
      </AppCard>
    </>
  );
}
